import React from 'react';
import { useStudioTypeSize } from '../StudioTypeScale';
import type { StudioPageContent } from '../../../constants';
import { studioCopy } from '../../../utils/contentCopy';
import StudioCtaRow from '../StudioCtaRow';
import LayoutFrame, { type LayoutContentProps } from './LayoutFrame';

interface CtaContentProps extends Partial<LayoutContentProps> {
  content: StudioPageContent;
  format: string;
  locale?: string;
  qrCodeUrl?: string;
}

const CtaContent: React.FC<CtaContentProps> = ({ content, format, locale, tokens, qrCodeUrl }) => {
  const isStory = format === 'story';
  const t = tokens!;
  const typeSize = useStudioTypeSize();
  const steps = Array.isArray(content.bullets) ? content.bullets : [];

  return (
    <div>
      {content.kicker && (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '5px 12px',
            marginBottom: isStory ? 24 : 16,
            borderRadius: t.radius.pill,
            color: t.onAccent,
            background: t.accent,
            fontSize: typeSize(10),
            fontWeight: 700,
            letterSpacing: 0.8,
            textTransform: 'uppercase'
          }}
        >
          {content.kicker}
        </div>
      )}
      <div
        style={{
          maxWidth: 312,
          color: t.ink,
          fontSize: typeSize(isStory ? 32 : 25.5),
          fontWeight: 700,
          letterSpacing: '-1px',
          lineHeight: 1.24
        }}
      >
        {content.title}
      </div>
      {content.subtitle && (
        <div
          style={{
            maxWidth: 300,
            marginTop: isStory ? 18 : 14,
            color: t.muted,
            fontSize: typeSize(isStory ? 14 : 12.5),
            fontWeight: 500,
            lineHeight: 1.68
          }}
        >
          {content.subtitle}
        </div>
      )}
      {steps.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: isStory ? 14 : 12,
            marginTop: isStory ? 28 : 22
          }}
        >
          {steps.map((step, index) => (
            <div
              key={`${index}-${step}`}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 11,
                color: t.ink,
                fontSize: typeSize(isStory ? 13 : 11.5),
                fontWeight: 600,
                lineHeight: 1.55
              }}
            >
              <span
                style={{
                  width: 22,
                  height: 22,
                  flexShrink: 0,
                  borderRadius: t.radius.pill,
                  background: t.accent,
                  color: t.onAccent,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>{step}</span>
            </div>
          ))}
        </div>
      )}
      <StudioCtaRow qrCodeUrl={qrCodeUrl} isStory={isStory} style={{ marginTop: isStory ? 26 : 20 }}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            maxWidth: 220,
            minWidth: 0,
            gap: 8,
            padding: isStory ? '14px 26px' : '12px 22px',
            borderRadius: t.radius.pill,
            background: t.accent,
            color: t.onAccent,
            fontSize: typeSize(isStory ? 13 : 12),
            fontWeight: 700,
            letterSpacing: 0.3,
            boxShadow: t.shadow,
            overflow: 'hidden'
          }}
        >
          <span
            style={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {content.actionLabel || studioCopy.learnMore(locale)}
          </span>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </div>
      </StudioCtaRow>
    </div>
  );
};

interface CtaLayoutProps {
  width: number;
  height: number;
  format: string;
  locale?: string;
  content?: StudioPageContent;
  themeColor?: string;
  profileName?: string;
  qrCodeUrl?: string;
}

const CtaLayout: React.FC<CtaLayoutProps> = props => (
  <LayoutFrame {...props}>
    <CtaContent content={props.content || {}} format={props.format} locale={props.locale} qrCodeUrl={props.qrCodeUrl} />
  </LayoutFrame>
);

export default CtaLayout;
